import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, Search, Filter, Loader2, Layers, LineChart } from 'lucide-react';
import { api } from '../../lib/api';

const STATUS_STYLES = {
  draft:    'text-white/70 border-white/15 bg-white/[0.03]',
  sent:     'text-cyan-300 border-cyan-400/30 bg-cyan-400/10',
  accepted: 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10',
  rejected: 'text-red-300 border-red-400/30 bg-red-400/10',
  expired:  'text-yellow-300 border-yellow-400/30 bg-yellow-400/10',
};

const QuotesListPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (productFilter) params.product = productFilter;
      if (statusFilter) params.status = statusFilter;
      const r = await api.get('/quotes', { params });
      setQuotes(r.data);
    } catch (e) { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [productFilter, statusFilter]);

  const filtered = quotes.filter((qu) => !q ||
    qu.quote_number.toLowerCase().includes(q.toLowerCase()));

  const fmt = (n, cur) => new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
            <FileText className="w-3.5 h-3.5" /> Quotes
          </div>
          <h1 className="mt-2 text-3xl font-semibold text-white tracking-tight">Quotations</h1>
          <p className="mt-1 text-white/55">All proposals with their current version and status. Click a quote to view versions and share.</p>
        </div>
        <Link to="/admin/quotes/new" className="h-10 px-4 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> New quotation
        </Link>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by quote number"
            className="h-9 pl-9 pr-3 w-64 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13px] focus:outline-none focus:border-cyan-400/50" />
        </div>
        <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
          className="h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13px] focus:outline-none focus:border-cyan-400/50">
          <option value="">All products</option>
          <option value="pti">PTI</option>
          <option value="core">Core</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13px] focus:outline-none focus:border-cyan-400/50">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-white/10 text-left text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50">
              <th className="px-6 py-4">Quote #</th>
              <th className="px-4 py-4">Product</th>
              <th className="px-4 py-4">Version</th>
              <th className="px-4 py-4">Status</th>
              <th className="px-4 py-4 text-right">Total</th>
              <th className="px-4 py-4">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-white/40"><Loader2 className="w-4 h-4 animate-spin inline-block" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-white/40">No quotes yet. Create your first quotation.</td></tr>
            ) : filtered.map((qu) => (
              <tr key={qu.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]">
                <td className="px-6 py-4">
                  <Link to={`/admin/quotes/${qu.id}`} className="font-mono text-cyan-300 hover:text-cyan-200 font-semibold">
                    {qu.quote_number}
                  </Link>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2 py-1 rounded-full border ${
                    qu.product === 'pti' ? 'text-cyan-300 border-cyan-400/30 bg-cyan-400/10' : 'text-purple-300 border-purple-400/30 bg-purple-400/10'
                  }`}>
                    {qu.product === 'pti' ? <LineChart className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
                    {qu.product.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-4 text-white/80 tabular-nums">v{qu.current_version_number}</td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border capitalize ${STATUS_STYLES[qu.status] || STATUS_STYLES.draft}`}>
                    {qu.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-right text-white font-semibold tabular-nums">{fmt(qu.latest_total, qu.currency)}</td>
                <td className="px-4 py-4 text-white/60 text-[12.5px]">{new Date(qu.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' })}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
};

export default QuotesListPage;
